import { debounce } from 'throttle-debounce';
import FilterBtn from './filter-btn';
import ToggleOpen from './toggle-open';
import CategoryList from './category-list';
import PaginationAPC from './pagination-apc';
import SortingOptions from './sorting-options';
import SelectedFacetsListItem from '../../blocks_common/selected-facets-list/index';

import {
    parseUrlBySlash,
    getLocale,
    setOpenState,
    syncOpenState,
    manageElementClassByState,
    manageElementClassListByState,
    tryGetAjaxParamUrlForCurrentLocation,
    tryAddEventListener,
    tryGetTarget,
    tryGetAjaxParam,
} from '../js-helpers/js-helpers';

import {
    LOAD_MORE_HANDLING_OPTION,
    MOBILE_FILTERS_APPLY_HANDLING_OPTION,
    MOBILE_FILTERS_SHOW_RESULTS,
    DEFAULT_HANDLING_OPTION,
    FAMILY_FILTERS_REQUEST,
} from '../constants/requestHandlingOptions';

const selectedFiltersAmountId = 'selectedFiltersAmount';
const mobileSRPFilterClassName = 'mobile-srp-filter-popup';
const stuckClassName = 'stuck';
const showResultsButtonClassName = 'show-results-btn';
const showResultsButtonSelector = `.${showResultsButtonClassName}`;
const mobileSRPFilterSelector = `.${mobileSRPFilterClassName}`;

const isFilterListItemOpen = filterListItemElement => {
    const btn = !!filterListItemElement && filterListItemElement.querySelector('.filter-list__btn');
    return btn && btn.classList.contains('open');
};

const setFilterListItemOpenState = (isOpen, filterListItemElement) => {
    const elementsCollection =
        filterListItemElement && filterListItemElement.querySelectorAll
            ? filterListItemElement.querySelectorAll(
                '.filter-list__btn, .filter-list__item-content',
            )
            : [];
    [...elementsCollection].forEach(element => setOpenState(isOpen, element));
};

const getFilterListItemTitle = filterListItemElement => {
    const btnTitle = filterListItemElement
        ? filterListItemElement.querySelector('.filter-list__btn-title')
        : null;
    if (!btnTitle) return null;
    const textSpan = btnTitle.querySelector('span');
    return (textSpan ? textSpan.textContent : btnTitle.textContent) || null;
};

const tryFindFilterListItemByTitle = (title, filterListItemCollection) =>
    [...(filterListItemCollection || [])].find(
        filterListItem => getFilterListItemTitle(filterListItem) === title,
    );

const syncShowMoreButtonOpenState = (oldItem, newItem) => {
    if (!oldItem || !newItem) return;
    const contentClassName = 'filter-list__item-content';
    const contentSelector = `.${contentClassName}`;
    const showMoreAttribute = 'data-show-more';
    const showMoreBtnSelector = `[${showMoreAttribute}]`;
    const showMoreItemsClassName = 'show-more-items';
    const oldShowMoreBtn = oldItem.querySelector(showMoreBtnSelector);
    const newShowMoreBtn = newItem.querySelector(showMoreBtnSelector);
    if (!oldShowMoreBtn || !newShowMoreBtn) return;
    const oldItemContent = oldItem.querySelector(contentSelector);
    const newItemContent = newItem.querySelector(contentSelector);
    const hasBeenRevealed = oldItemContent.classList.contains(showMoreItemsClassName);
    manageElementClassByState(newItemContent, showMoreItemsClassName, hasBeenRevealed);
};

const syncPopupFiltersOpenState = (oldPopupFilter, newPopupFilter) => {
    if (!oldPopupFilter || !newPopupFilter) return;
    syncOpenState(oldPopupFilter, newPopupFilter);
    const oldFilterListItems = oldPopupFilter.querySelectorAll('.filter-list__item');
    const newFilterListItems = newPopupFilter.querySelectorAll('.filter-list__item');

    [...oldFilterListItems].forEach(oldItem => {
        const itemTitle = getFilterListItemTitle(oldItem);
        if (!itemTitle) return;
        const newItem = tryFindFilterListItemByTitle(itemTitle, newFilterListItems);
        if (newItem) {
            setFilterListItemOpenState(isFilterListItemOpen(oldItem), newItem);
            syncShowMoreButtonOpenState(oldItem, newItem);
        }
    });

    const oldShowResultsButton = oldPopupFilter.querySelector(showResultsButtonSelector);
    const newShowResultsButton = newPopupFilter.querySelector(showResultsButtonSelector);
    if (oldShowResultsButton && newShowResultsButton) {
        manageElementClassByState(
            newShowResultsButton,
            stuckClassName,
            oldShowResultsButton.classList.contains(stuckClassName),
        );
    }
    manageElementClassByState(
        newPopupFilter,
        'has-location-filter',
        !!newPopupFilter.querySelector('.location-filter'),
    );
};

const adjustStuckState = popupFilter => {
    if (!popupFilter) return;
    const showResultsBtn = popupFilter.querySelector(showResultsButtonSelector);
    if (!showResultsBtn) return;
    const { scrollTop, offsetHeight, scrollHeight } = popupFilter;
    const scrollBottom = scrollTop + offsetHeight;
    const delta = 30;
    const shouldBeStuck = scrollHeight - delta > scrollBottom;
    manageElementClassByState(showResultsBtn, stuckClassName, shouldBeStuck);
};

const adjustStuckStateDebounced = debounce(270, adjustStuckState);

const handlePopupFilterScroll = ({ target }) => adjustStuckStateDebounced(target);

const handleFilterButtonToggleClick = () => {
    const filterPopup = document.querySelector(mobileSRPFilterSelector);
    const animationTimeoutMs = 300; // pause duration to await when animation will be ended
    if (filterPopup) {
        setTimeout(() => adjustStuckStateDebounced(filterPopup), animationTimeoutMs);
    }
};

const bindFilterPopupScrollEventHandling = () => {
    const filterPopup = document.querySelector(mobileSRPFilterSelector);
    if (filterPopup) {
        filterPopup.removeEventListener('scroll', handlePopupFilterScroll);
        filterPopup.addEventListener('scroll', handlePopupFilterScroll);
    }
};

const isShowResultsButton = element =>
    !!element && !!tryGetTarget(element, showResultsButtonClassName);

const refreshShowResultsBtnAjaxParamState = () => {
    const showResultsButton = document.querySelector(showResultsButtonSelector);
    if (showResultsButton) {
        const ajaxParam = tryGetAjaxParamUrlForCurrentLocation();
        if (ajaxParam) {
            showResultsButton.setAttribute('data-ajax-param', ajaxParam);
        }
    }
};

export default class SRP_Search {
    constructor() {
        this.BLOCK = null;
        this.BLOCK_INNER = null;

        this.CATEGORY_COLLECTION = null;
        this.FILTER_BTN_COLLECTION = null;
        this.MOBILE_TOGGLE_OPEN_COLLECTION = null;
        this.SORTER_COLLECTION = null;
        this.SELECTED_FACETS_COLLECTION = null;
        this.PAGINATION_COLLECTION = null;
        this.LOCATION_FILTER = null;
        this.LOCATION_FILTER_MOB = null;

        this.AJAX_SRC = null;
        this.LOADER = null;
        this.SCROLL_TO_TOP_HARD = false;

        this.tmpArraysObj = {
            btnInstancesArr: [],
            toggleOpenInstancesArr: [],
            categoryListInstancesArr: [],
            selectedFacetsItemInstancesArr: [],
            paginationInstanceArr: [],
            sortingInstancesArr: [],
        };

        this.FIRST_ENTER = true;
        this.LAST_SELECTED_FACET_NAME = null;
    }

    /**
     * Inits instance (looks for needed DOM nodes and attaches eventListeners)
     * @param node {Object} - DOM node
     */
    init(node) {
        if (!this.BLOCK) this.BLOCK = node;
        if (!this.BLOCK) throw new Error('node is not passed to init method of instance');

        this.AJAX_SRC = this.BLOCK.dataset.ajaxSource || 'ajax-source is not set';

        const scrollToTopParam = this.BLOCK.dataset.scrollToTop;
        if (scrollToTopParam === 'true') this.SCROLL_TO_TOP_HARD = true;

        this.FILTER_BTN_COLLECTION = this.BLOCK.querySelectorAll('.js-filter-list__item');
        this.FILTER_BTN_ARRAY = [...this.FILTER_BTN_COLLECTION];

        this.MOBILE_TOGGLE_OPEN_COLLECTION = this.BLOCK.querySelectorAll('.js-toggle-open-btn');
        this.MOBILE_TOGGLE_OPEN_ARRAY = [...this.MOBILE_TOGGLE_OPEN_COLLECTION];

        this.CATEGORY_COLLECTION = this.BLOCK.querySelectorAll('.js-category-list');
        this.CATEGORY_ARRAY = [...this.CATEGORY_COLLECTION];

        this.SORTER_COLLECTION = this.BLOCK.querySelectorAll('.js-sorting-list__item');
        this.SORTER_ARRAY = [...this.SORTER_COLLECTION];

        this.LOADERS = document.querySelectorAll('.loader');

        this.SELECTED_FACETS_COLLECTION = this.BLOCK.querySelectorAll(
            '.js-selected-facets__list-item',
        );
        this.SELECTED_FACETS_ARRAY = [...this.SELECTED_FACETS_COLLECTION];

        this.PAGINATION_COLLECTION = this.BLOCK.querySelectorAll('.js-pagination-apc');
        this.PAGINATION_ARRAY = [...this.PAGINATION_COLLECTION];

        this.LOCATION_FILTER = this.BLOCK.querySelector('.js-location-filter');
        this.LOCATION_FILTER_MOB = this.BLOCK.querySelector('.js-location-filter-mob');

        if (this.FIRST_ENTER) {
            this.FIRST_ENTER = false;
            const currentSearchParam = location.search;
            if (location.search) {
                history.pushState(
                    {
                        page: currentSearchParam,
                        type: 'page',
                    },
                    null,
                    currentSearchParam,
                );
            }
        }

        if (this.FILTER_BTN_ARRAY && this.FILTER_BTN_ARRAY.length > 0) {
            this.handleTargetNodesArrays(
                this.FILTER_BTN_ARRAY,
                this.tmpArraysObj.btnInstancesArr,
                FilterBtn,
            );

            const categoryListElement = tryFindFilterListItemByTitle('Category', [
                ...document.querySelectorAll(`${mobileSRPFilterSelector} .js-filter-list__item`),
            ]);

            if (categoryListElement) {
                manageElementClassByState(categoryListElement, 'popup-category-list', true);
            }
        }

        if (this.SORTER_ARRAY && this.SORTER_ARRAY.length > 0) {
            this.handleTargetNodesArrays(
                this.SORTER_ARRAY,
                this.tmpArraysObj.sortingInstancesArr,
                SortingOptions,
            );
        }

        if (this.MOBILE_TOGGLE_OPEN_ARRAY && this.MOBILE_TOGGLE_OPEN_ARRAY.length > 0) {
            this.handleTargetNodesArrays(
                this.MOBILE_TOGGLE_OPEN_ARRAY,
                this.tmpArraysObj.toggleOpenInstancesArr,
                ToggleOpen,
            );
        }

        if (this.CATEGORY_ARRAY && this.CATEGORY_ARRAY.length > 0) {
            this.handleTargetNodesArrays(
                this.CATEGORY_ARRAY,
                this.tmpArraysObj.categoryListInstancesArr,
                CategoryList,
            );
        }

        if (this.SELECTED_FACETS_ARRAY && this.SELECTED_FACETS_ARRAY.length > 0) {
            this.handleTargetNodesArrays(
                this.SELECTED_FACETS_ARRAY,
                this.tmpArraysObj.selectedFacetsItemInstancesArr,
                SelectedFacetsListItem,
            );
        }

        if (this.PAGINATION_ARRAY && this.PAGINATION_ARRAY.length > 0) {
            this.handleTargetNodesArrays(
                this.PAGINATION_ARRAY,
                this.tmpArraysObj.paginationInstanceArr,
                PaginationAPC,
            );
        }

        this.BLOCK.addEventListener('click', this.handleMediatorClick);
        this.BLOCK.addEventListener('ajax-src-container-click', this.handleAjaxSrcContainerClick);
        this.BLOCK.addEventListener('ajax-load-more-click', this.handleAjaxLoadMoreClick);

        if (this.LOCATION_FILTER) {
            this.LOCATION_FILTER.addEventListener('click', this.handleLocationFilterClick);
        }
        if (this.LOCATION_FILTER_MOB) {
            this.LOCATION_FILTER_MOB.addEventListener('click', this.handleLocationFilterMobClick);
        }

        const fetchEventOptions = {
            bubbles: true,
            cancelable: false,
        };

        this.fetchStart = new CustomEvent('fetchStart', fetchEventOptions);
        this.fetchEnd = new CustomEvent('fetchEnd', fetchEventOptions);
        this.fetchStartFromFamilyFilter = new CustomEvent(
            'fetchStartFromFamilyFilter',
            fetchEventOptions,
        );

        document.addEventListener(
            'fetchStartFromFamilyInput',
            this.handleFetchStartFromFamilyInput,
        );

        this.filterPopupFetchStart = new CustomEvent('fetchStart', {
            ...fetchEventOptions,
            detail: { isPopupFilter: true },
        });

        document.addEventListener('fetchStart', this.handleFetchStart);
        document.addEventListener('fetchEnd', this.handleFetchEnd);
        document.addEventListener(
            'fetchStartFromFamilyFilter',
            this.handleFetchStartFromFamilyFilter,
        );

        window.addEventListener('popstate', this.handleBrowserNavigation);
        bindFilterPopupScrollEventHandling();

        const showResultsBtn = document.querySelector(showResultsButtonSelector);

        if (showResultsBtn) {
            showResultsBtn.addEventListener('click', ({ target }) => {
                const button = tryGetTarget(target, showResultsButtonClassName);
                if (!button) return;
                const ajaxParam = tryGetAjaxParam(button);
                if (!ajaxParam) return;
                target.dispatchEvent(
                    new CustomEvent('ajax-src-container-click', {
                        bubbles: true,
                        detail: { ajaxParam },
                    }),
                );
            });

            document.removeEventListener('filter-button-click', handleFilterButtonToggleClick);
            document.addEventListener('filter-button-click', handleFilterButtonToggleClick);
        }

        const filter = document.querySelector(mobileSRPFilterSelector);
        if (filter) {
            adjustStuckStateDebounced(filter);
            const modalClassList = ['modal-open', 'popup-filter-modal-open'];
            tryAddEventListener(filter, 'content-open', () => {
                manageElementClassListByState(document.body, modalClassList, true);
                adjustStuckStateDebounced(filter);
            });
            tryAddEventListener(filter, 'content-close', () =>
                manageElementClassListByState(document.body, modalClassList, false),
            );
            manageElementClassListByState(
                document.body,
                modalClassList,
                filter.classList.contains('open'),
            );
            manageElementClassByState(
                filter,
                'has-location-filter',
                !!filter.querySelector('.location-filter'),
            );
        }
    }

    /**
     * Reinits instance
     */
    reinit() {
        // this.BLOCK.removeEventListener('click', this.handleMediatorClick);
        // this.BLOCK.removeEventListener('ajax-src-container-click', this.handleAjaxSrcContainerClick);
        // document.removeEventListener('fetchStart', this.handleFetchStart);
        // document.removeEventListener('fetchEnd', this.handleFetchEnd);

        this.init();
    }

    /**
     * Creates and sends request to server
     * returns es6 fetch
     * after successful resolve it calls handleResponse() class method
     * @param ajaxParam {String} - ajax param
     * @param option {String} - defines the way to handle request and/or response data
     * @param scrollToTop {Boolean} - whether to scroll after response handling or not
     * @param isBack {Boolean}- if user went to page via BackForward functionality do not add this request in history
     */
    sendRequest(ajaxParam, option = DEFAULT_HANDLING_OPTION, scrollToTop = false, isBack = false) {
        if (!ajaxParam) {
            console.warn('There is no valid "ajaxParam" parameter passed');
            return;
        }

        const source = `${this.AJAX_SRC}${ajaxParam}`;

        const fetchHeaders = new Headers({ 'Content-Type': 'text/html' });

        if (
            option === MOBILE_FILTERS_APPLY_HANDLING_OPTION ||
            option === MOBILE_FILTERS_SHOW_RESULTS
        ) {
            document.dispatchEvent(this.filterPopupFetchStart);
        } else {
            document.dispatchEvent(this.fetchStart);
        }

        return fetch(source /* this.AJAX_SRC */, {
            method: 'get',
            credentials: 'same-origin',
            headers: fetchHeaders,
            cache: 'default',
        })
            .then(response => {
                if (!response.ok) throw new Error(response.statusText);
                return response.text();
            })
            .then(result => {
                this.handleResponse(result, option);
                return result;
            })
            .then(result => {
                if (scrollToTop || this.SCROLL_TO_TOP_HARD) {
                    window.scrollTo(0, 0);
                }
                // To prevent double search parameter in URL
                const data = parseUrlBySlash();
                const locale = getLocale(data);
                const newSource = `${locale}/${source}`;
                if (!isBack) {
                    history.pushState(
                        {
                            page: ajaxParam,
                            type: 'page',
                        },
                        null,
                        newSource,
                    );
                }
            })
            .then(refreshShowResultsBtnAjaxParamState)
            .catch(error => {
                document.dispatchEvent(this.fetchEnd);
                console.error(error);
                if (scrollToTop) {
                    window.scrollTo(0, 0);
                }
            });
    }

    /**
     * Handles target nodes arrays ( such as:
     *      this.CATEGORY_COLLECTION= null;
     *      this.FILTER_BTN_COLLECTION = null;
     *      this.MOBILE_BTN_COLLECTION = null;
     * )
     * @param nodesArray { Array } - array of DOM nodes
     * @param nodesStorage { Array } - storage where instances are
     * @param instanceConstructor - { Class | Function } class or constructor function to use to create instances
     */
    handleTargetNodesArrays(nodesArray, nodesStorage, instanceConstructor) {
        if (nodesArray && nodesArray.length > 0) {
            if (nodesStorage.length > 0) {
                nodesStorage.forEach((curInstance, i, arr) => {
                    curInstance.instance.destroy();
                });
                while (nodesStorage.length) {
                    // Important! Remove all old instances to be destroyed by garbage collector
                    // Do not write [] to the nodesStorage, cause you will lose ref to the original array!
                    nodesStorage.pop();
                }
            }

            nodesArray.forEach((item, i, arr) => {
                nodesStorage.push({
                    instance: new instanceConstructor(item),
                    item,
                });
            });

            nodesStorage.forEach((curInstance, i, arr) => {
                curInstance.instance.init(curInstance.item);
            });
        }
    }

    /**
     * Default handleResponse class method
     * @param result {String} - html document from server
     * @param option {String} - defines the way to handle current response data
     */
    handleResponse(result, option = DEFAULT_HANDLING_OPTION) {
        const frag = document.createRange().createContextualFragment(result);

        switch (option) {
            case LOAD_MORE_HANDLING_OPTION:
                this.handleResponseLoadMore(frag);
                break;

            case MOBILE_FILTERS_APPLY_HANDLING_OPTION:
                this.handleResponseFiltersApply(frag);
                break;

            case FAMILY_FILTERS_REQUEST:
                this.handleResponseDefault(frag);
                break;

            case MOBILE_FILTERS_SHOW_RESULTS:
            default:
                this.handleResponseDefault(frag);
                break;
        }

        document.dispatchEvent(this.fetchEnd);
    }

    /**
     * Handles responses in case of 'loadMore' option
     * @param frag { DocumentFragment } - response markup document model
     * */
    handleResponseLoadMore(frag) {
        const originalListBlock = this.BLOCK.querySelector('.result-list');
        const originalPaginationAPCBlock = this.BLOCK.querySelector('.js-pagination-apc');

        const resultListBlockItems = frag.querySelectorAll('.result-list__item');
        const paginationAPCBlock = frag.querySelector('.js-pagination-apc');

        const tmpDocFrag = document.createDocumentFragment();

        [...resultListBlockItems].forEach(argItem => {
            const isNode = argItem instanceof Node;
            tmpDocFrag.appendChild(isNode ? argItem : document.createTextNode(String(argItem)));
        });

        const firstIndex = this.BLOCK.querySelector(
            '.search-result-indication-message__quantity_mobile span',
        ).innerHTML;

        originalListBlock.appendChild(tmpDocFrag);
        originalPaginationAPCBlock.replaceWith(paginationAPCBlock);

        this.PAGINATION_COLLECTION = this.BLOCK.querySelectorAll('.js-pagination-apc');
        this.PAGINATION_ARRAY = [...this.PAGINATION_COLLECTION];

        if (this.PAGINATION_ARRAY && this.PAGINATION_ARRAY.length > 0) {
            this.handleTargetNodesArrays(
                this.PAGINATION_ARRAY,
                this.tmpArraysObj.paginationInstanceArr,
                PaginationAPC,
            );
        }
        this.BLOCK.querySelector(
            '.search-result-indication-message__quantity_mobile span',
        ).innerHTML = firstIndex;
    }

    /**
     * Handles responses if initiated from mobile filter popup
     * @param frag { DocumentFragment } - response markup document model
     * */
    handleResponseFiltersApply(frag) {
        if (!frag) {
            console.error('Result markup fragment is absent');
            return;
        }
        const newPopupFilter = frag.querySelector(mobileSRPFilterSelector);
        const oldPopupFilter = document.querySelector(mobileSRPFilterSelector);

        if (!newPopupFilter) {
            console.error('Error on filters applying: incorrect result markup');
            return;
        }

        if (!oldPopupFilter) {
            console.error('Old popup filter is absent');
            return;
        }

        const scrollTopPosition = oldPopupFilter.scrollTop;
        let offsetDelta = 0;
        const oldFacetOffsetTop = this.tryGetLastSelectedFacetOffsetTop(oldPopupFilter);
        const oldSelectedFacets = oldPopupFilter.querySelector('.selected-facets__list');
        const facetsScrollLeftPosition = oldSelectedFacets ? oldSelectedFacets.scrollLeft : 0;
        const wrapper = oldPopupFilter.parentNode;

        syncPopupFiltersOpenState(oldPopupFilter, newPopupFilter);
        wrapper.replaceChild(newPopupFilter, oldPopupFilter);

        const newFacetOffsetTop = this.tryGetLastSelectedFacetOffsetTop(newPopupFilter);
        if (oldFacetOffsetTop && newFacetOffsetTop) {
            offsetDelta = newFacetOffsetTop - oldFacetOffsetTop;
        }

        newPopupFilter.scroll({ top: scrollTopPosition + offsetDelta });
        const selectedFacets = newPopupFilter.querySelector('.selected-facets__list');
        if (selectedFacets && selectedFacets.scroll) {
            selectedFacets.scroll({ left: facetsScrollLeftPosition });
        }

        this.reinit();
    }

    /**
     * Fallback function to handle response data
     * @param frag { DocumentFragment } - response markup document model
     * */
    handleResponseDefault(frag) {
        const insertBlock = frag.querySelector('.js-srp-mediator-inner');

        this.BLOCK.innerHTML = '';
        this.BLOCK.appendChild(insertBlock);

        this.reinit();
    }

    /**
     * Returns offset top position of the last used facet for current popup filter state
     * @param filterPopup {Node} - filter popup DOM element
     * @returns {number} - offset number
     */
    tryGetLastSelectedFacetOffsetTop = filterPopup => {
        if (this.LAST_SELECTED_FACET_NAME) {
            const facet = tryFindFilterListItemByTitle(
                this.LAST_SELECTED_FACET_NAME,
                filterPopup.querySelectorAll('.filter-list__item'),
            );
            return (facet && facet.offsetTop) || 0;
        }
        return 0;
    };

    /**
     * According to condition if popup clicked, resolves appropriate handling option
     * @param targetElement { EventTarget } - DOM element
     * @returns { string } - handling option constant
     */
    resolveHandlingOption(event) {
        if (
            event.detail.option &&
            event.detail.option === FAMILY_FILTERS_REQUEST &&
            (this.isFamilyFilter(event.target) || document.querySelector('.ui-selectmenu-open'))
        ) {
            if (
                !document.querySelector('#hardware-model .modelNumber-input').getAttribute('value')
            ) {
                return FAMILY_FILTERS_REQUEST;
            }

            return DEFAULT_HANDLING_OPTION;
        }

        if (isShowResultsButton(event.target)) {
            return MOBILE_FILTERS_SHOW_RESULTS;
        }
        if (this.isPopupFilter(event.target) && !this.isLastChips(event.target)) {
            return MOBILE_FILTERS_APPLY_HANDLING_OPTION;
            // eslint-disable-next-line no-else-return
        } else if (this.isLastChips(event.target)) {
            return MOBILE_FILTERS_SHOW_RESULTS;
        }

        return DEFAULT_HANDLING_OPTION;
    }

    /**
     * Determines if targetElement is the popup filter or into the popup filter
     * @param targetElement { EventTarget } - DOM element
     * @returns { Boolean } - true if it's the popup filter or into it, otherwise - false
     * */
    isPopupFilter(targetElement) {
        const popup = document.querySelector(mobileSRPFilterSelector);
        return !!popup && (popup === targetElement || popup.contains(targetElement));
    }

    isFamilyFilter(targetElement) {
        return (
            targetElement.querySelector('#hardware-model') ||
            targetElement.querySelector('#hardware-model-mob')
        );
    }

    // eslint-disable-next-line class-methods-use-this
    isLastChips(targetElement) {
        return (
            targetElement.classList.contains('selected-facets__list-item') &&
            !targetElement.classList.contains('js-tooltip-acceptor')
        );
    }

    /**
     * Handles click within this.BLOCK
     * @param e {Event} - event
     */
    handleMediatorClick = e => {
        let { target } = e;

        const handlingOption = this.resolveHandlingOption(e);

        if (handlingOption === MOBILE_FILTERS_APPLY_HANDLING_OPTION) {
            const currentFacet = tryGetTarget(target, 'filter-list__item');
            if (currentFacet) {
                this.LAST_SELECTED_FACET_NAME = getFilterListItemTitle(currentFacet);
            } else {
                this.LAST_SELECTED_FACET_NAME = null;
            }
        }

        if (
            target.classList.contains('filter-list-wrap__clear') ||
            target.classList.contains('js-clear-selected-facets')
        ) {
            const { ajaxParam } = target.dataset;
            if (ajaxParam) this.sendRequest(ajaxParam, handlingOption);
        }
        if (target.classList.contains('mobile-list-btn__close')) {
            const { ajaxParam } = target.dataset;
            if (ajaxParam) this.sendRequest(ajaxParam, handlingOption);
        } else if (
            !target.classList.contains('mobile-list-btn__close') &&
            target.closest('.mobile-list-btn__close')
        ) {
            target = target.closest('.mobile-list-btn__close');

            const { ajaxParam } = target.dataset;
            if (ajaxParam) this.sendRequest(ajaxParam, handlingOption);
        }
    };

    // TODO: rewrite similar code in the methods below
    /**
     * Handles click within location-filter
     * @param e {Object} - event
     */
    handleLocationFilterClick = e => {
        const locationFilterClass = 'js-location-filter';
        this.handleLocationFilterClickBase(e, locationFilterClass);
    };

    /**
     * Handles click within location-filter
     * @param e {Object} - event
     */
    handleLocationFilterMobClick = e => {
        const locationFilterClass = 'js-location-filter-mob';
        this.handleLocationFilterClickBase(e, locationFilterClass);
    };

    handleLocationFilterClickBase = debounce(100, (event, locationFilterClass) => {
        const target = tryGetTarget(event.target, locationFilterClass);
        if (!target) return;
        const ajaxParam = tryGetAjaxParam(target);
        const handlingOption = this.resolveHandlingOption(event);
        if (ajaxParam) this.sendRequest(ajaxParam, handlingOption);
    });

    /**
     * Handles ajax source container click
     * @param e {Object} - event
     */
    handleAjaxSrcContainerClick = e => {
        this.sendRequest(e.detail.ajaxParam, this.resolveHandlingOption(e), e.detail.scrollToTop);
    };

    /**
     * Handles ajax source container click
     * @param e {Object} - event
     */
    handleAjaxLoadMoreClick = e => {
        this.sendRequest(e.detail.ajaxParam, LOAD_MORE_HANDLING_OPTION);
    };

    /**
     * Handles fetchStart event
     * @param e {Object} - event
     */
    handleFetchStart = e => {
        this.LOADER = [...this.LOADERS].filter(item => item.className === 'loader').shift();

        if (this.LOADER) {
            this.LOADER.classList.add('show');

            if (e.detail && e.detail.isPopupFilter === true) {
                /* explicit bool check */
                this.LOADER.classList.add('over-popup');
            }
        }
    };

    handleFetchStartFromFamilyFilter = e => {
        if (window.innerWidth < 960) {
            this.LOADER_FAMILY_MOB = document.querySelector('#hardware-loader-mob');

            if (this.LOADER_FAMILY_MOB) {
                this.LOADER_FAMILY_MOB.classList.add('show');
            }
        }

        this.LOADER_FAMILY = document.querySelector('#hardware-loader');

        if (this.LOADER_FAMILY) {
            this.LOADER_FAMILY.classList.add('show');
        }
    };

    /**
     * Handles fetchEnd event
     * @param e - event
     */
    handleFetchEnd = () => {
        [...this.LOADERS].forEach(item => {
            if (item.classList.contains('show')) {
                item.classList.remove('show');
            }

            if (item.classList.contains('over-popup')) {
                item.classList.remove('over-popup');
            }
        });
    };

    /**
     * Default handleBrowserNavigation class method
     * @param e {Object} - event
     */
    handleBrowserNavigation = e => {
        if (e.state) {
            const state = e.state.page;
            this.sendRequest(state, this.resolveHandlingOption(e), true, true);
        }
    };
}
/*--------------------------------------------------------------------------------------------------------------------*/

